import React, { useState, useEffect } from 'react';
import { Home, Bell, Menu, LogOut, Building2 } from 'lucide-react';
import Sidebar from './Sidebar';
import { useAuth, useActiveEstId } from './AuthContext';
import ThemeSwitcher from './ThemeSwitcher';
import { employeeAPI, establishmentAPI, payrollAPI } from '../api/apiService';

// Imports — Establishments
import Establishments from './Establishments';
import AddEstablishment from './AddEstablishment';
import UpdateEstablishment from './UpdateEstablishment';

// Employee
import Employees from './Employees';
import NewEmployee from './NewEmployee';
import UpdateEmployee from './UpdateEmployee';
import MyProfile from './MyProfile';

// Reports
import Reports from './Reports';
import ReportsHub from './ReportsHub';
import PayrollInsights from './PayrollInsights';

// HR/Payroll Forms
import FormsTemplates from './FormsTemplates';
import ViewForms from './ViewForms';
import Forms from './Forms';
import FormDataReview from './Formdatareview';
import AppointmentOrderForm from './AppointmentOrderForm';
import FormSUTI from './FormSUTI';
import FormXIV from './FormXIV';
import Form19Payslip from './Form19Payslip';
import MyPayslip from './MyPayslip';
import ViewStatements from './ViewStatements';
import FormTMusterRoll from './FormTMusterRoll';
import ECRGenerator from './ECRGenerator';
import FormXRegisterWages from './FormXRegisterWages';
import FormXIIIMinimumWages from './FormXIIIMinimumWages';
import BankStatementForm from './BankStatementForm';
import FormXXIIIOvertime from './FormXXIIIOvertime';
import Form5EarnedLeave from './Form5EarnedLeave';
import FormUAnnualReturns from './FormUAnnualReturns';
import FormXXIXDeductionsExcel from './FormXXIXDeductionsExcel';
import FormAProfessionalTax from './FormAProfessionalTax';

// Leave
import ApplyLeave from './ApplyLeave';
import ApproveLeave from './ApproveLeave';

// Analytics
import Analytics from './Analytics';

// Resignation
import ApplyResignation from './ApplyResignation';
import ApproveResignation from './ApproveResignation';
import TerminateEmployee from './TerminateEmployee';

// Finance
import Adjustments from './Adjustments';
import VariablePay from './VariablePay';

// Workforce
import OvertimeWork from './OvertimeWork';
import ShiftTracker from './ShiftTracker';
import HolidaySchedule from './HolidaySchedule';
import AttendanceRegistry from './AttendanceRegistry';
import LeaveAbsentRecord from './LeaveAbsentRecord';
import MonthlyPayrollRegistry from './MonthlyPayrollRegistry';

// Admin
import AdminDashboard from './AdminDashboard';
import LeaveConfigPage from './LeaveConfigPage';
import ChatPopup from './ChatPopup';
import GenerateInvoice from './GenerateInvoice';
import ViewInvoices from './ViewInvoices';
import Asset from './Asset';
import Documents from './Documents';
import AuditTrail from './AuditTrail';
import GroupsManagement from './GroupsManagement';

// Barcode Attendance
import AttendanceScannerKiosk from './AttendanceScannerKiosk';
import ShiftLiveLog from './ShiftLiveLog';
import MyBarcode from './MyBarcode';
import MyShiftHistory from './MyShiftHistory';
import MyAttendance from './MyAttendance';

import { canAccessPage, getPrimaryRole } from '../utils/permissions';
import Unauthorized from './Unauthorized';
import InviteUsers from './InviteUsers';
import UserProfile from './UserProfile';
import OrgSwitcher from './OrgSwitcher';
import './Dashboard.css';

// ── Page metadata for breadcrumb & titles ──────────────────────────
const VIEW_META = {
  'dashboard':               { title: 'Dashboard',               section: null },
  'organizations':          { title: 'Organizations',           section: 'Organization' },
  'add-organization':       { title: 'New Organization',        section: 'Organization' },
  'update-organization':    { title: 'Manage Organizations',    section: 'Organization' },
  'employees':               { title: 'Employees',               section: 'Organization' },
  'new-employee':            { title: 'New Employee',            section: 'Organization' },
  'update-employee':         { title: 'Manage Employees',        section: 'Organization' },
  'attendance-registry':     { title: 'Attendance Registry',     section: 'Workforce' },
  'shift-tracker':           { title: 'Shift Tracker',           section: 'Workforce' },
  'holiday-schedule':        { title: 'Holiday Schedule',        section: 'Workforce' },
  'leave-absent-record':     { title: 'Leave & Absent Record',   section: 'Workforce' },
  'overtime-work':           { title: 'Overtime Management',     section: 'Workforce' },
  'monthly-info-service':    { title: 'Payroll Insights',        section: 'Workforce' },
  'generate-payroll':        { title: 'Process Payroll',         section: 'Payroll' },
  'monthly-payroll-registry':{ title: 'Monthly Payroll Registry',section: 'Payroll' },
  'payslip':                 { title: 'View Payslips',           section: 'Payroll' },
  'view-statements':         { title: 'View Statements',         section: 'Payroll' },
  'adjustments':              { title: 'Adjustments',             section: 'Payroll' },
  'variable-pay':            { title: 'Variable Pay',            section: 'Payroll' },
  'forms-templates':         { title: 'Generate Forms',          section: 'Statutory Forms' },
  'view-forms':              { title: 'View Forms',              section: 'Statutory Forms' },
  'appointment-order':       { title: 'Appointment Order',       section: 'Statutory Forms' },
  'form-s-uti':              { title: 'Form S – UAN Declaration',section: 'Statutory Forms' },
  'form-xiv':                { title: 'Form XIV – Employment Card',section:'Statutory Forms' },
  'form-19-payslip':         { title: 'Form 19 – Payslip',       section: 'Statutory Forms' },
  'form-t-muster-roll':      { title: 'Form T – Muster Roll',    section: 'Statutory Forms' },
  'ecr-generator':           { title: 'ECR Generator',           section: 'Statutory Forms' },
  'form-x-wages':            { title: 'Form X – Register of Wages', section: 'Statutory Forms' },
  'form-xiii-min-wages':     { title: 'Form XIII – Minimum Wages', section: 'Statutory Forms' },
  'bank-statement':          { title: 'Bank Statement',          section: 'Statutory Forms' },
  'form-xxiii-overtime':     { title: 'Form XXIII – Overtime',   section: 'Statutory Forms' },
  'form-5-earned-leave':     { title: 'Form 5 – Earned Leave',   section: 'Statutory Forms' },
  'form-u-annual-returns':   { title: 'Form U – Annual Returns', section: 'Statutory Forms' },
  'form-xxix-deductions':    { title: 'Form XXIX – Deductions',  section: 'Statutory Forms' },
  'form-a-professional-tax': { title: 'Form A – Professional Tax', section: 'Statutory Forms' },
  'apply-leave':             { title: 'Apply Leave',             section: 'Leave' },
  'approve-leave':           { title: 'Approve Leave',           section: 'Leave' },
  'analytics':               { title: 'Analytics',               section: 'Workforce' },
  'apply-resignation':       { title: 'Apply Resignation',       section: 'Employment' },
  'approve-resignation':     { title: 'Approve Resignation',     section: 'Employment' },
  'terminate-employee':      { title: 'Terminate Employee',      section: 'Employment' },
  'leave-settings':          { title: 'Leave Settings',          section: 'Leave' },
  'generate-invoice':        { title: 'Create Invoice',          section: 'Finance' },
  'view-invoices':           { title: 'View Invoices',           section: 'Finance' },
  'assets':                  { title: 'Assets',                  section: 'Finance' },
  'reports':                 { title: 'Reports & Analytics',     section: 'Insights' },
  'documents':               { title: 'Documents',               section: 'Compliance' },
  'admin-dashboard':         { title: 'Admin Dashboard',         section: 'Admin' },
  'audit-trail':             { title: 'Audit Trail',             section: 'Admin' },
  'groups-management':       { title: 'Groups',                  section: 'Admin' },
  'invite-users':            { title: 'Users',                   section: 'Admin' },
  'profile':                 { title: 'My Profile',              section: null },
  // Barcode Attendance
  'barcode-scanner':         { title: 'Barcode Scanner',         section: 'Workforce' },
  'shift-live-log':          { title: 'Live Shift Log',          section: 'Workforce' },
  'my-barcode':              { title: 'My Barcode',              section: 'Organization' },
  'my-shift-history':        { title: 'My Attendance Logs',      section: 'Organization' },
  'my-attendance':           { title: 'My Attendance',           section: 'Workforce' },
};

const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
};

// ── KPI Card helper ─────────────────────────────────────────────────
const KpiCard = ({ icon, label, value, sub, colorClass }) => (
  <div className="dh-kpi-card">
    <div className="dh-kpi-header-row">
      <span className="dh-kpi-label">{label}</span>
      <div className={`dh-kpi-icon ${colorClass}`}>{icon}</div>
    </div>
    <div className="dh-kpi-body">
      <span className="dh-kpi-value">{value}</span>
      {sub && <span className="dh-kpi-sub">{sub}</span>}
    </div>
  </div>
);

// ── Quick Action Card helper ─────────────────────────────────────────
const ActionCard = ({ icon, title, desc, colorClass, onClick }) => (
  <button className="dh-action-card" onClick={onClick}>
    <div className={`dh-action-icon ${colorClass}`}>{icon}</div>
    <div className="dh-action-body">
      <span className="dh-action-title">{title}</span>
      <span className="dh-action-desc">{desc}</span>
    </div>
  </button>
);

// ── SVG icons (lightweight, no extra library needed) ────────────────
const IconEmployees = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const IconBuilding = () => <Building2 size={18} />;
const IconRupee = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"/>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);
const IconActivity = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);
const IconUserPlus = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="8.5" cy="7" r="4"/>
    <line x1="20" y1="8" x2="20" y2="14"/>
    <line x1="23" y1="11" x2="17" y2="11"/>
  </svg>
);
const IconCalendar = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconForms = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);
const IconChart = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);
const IconLeave = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
    <path d="m9 16 2 2 4-4"/>
  </svg>
);

// ── Main Dashboard Component ─────────────────────────────────────────
const Dashboard = () => {
  const { user, logout } = useAuth();
  const lockedEstId = useActiveEstId();
  const [currentView, setCurrentView] = useState(() => {
    const path = window.location.pathname.slice(1) || 'dashboard';
    return VIEW_META[path] ? path : 'dashboard';
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const [dashboardStats, setDashboardStats] = useState({
    totalEmployees: 0, activeEmployees: 0,
    activeEstablishments: 0, currentMonthPayroll: 0, loading: true,
  });

  // Resize handler
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Sync view with browser back/forward
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname.slice(1) || 'dashboard';
      setCurrentView(VIEW_META[path] ? path : 'dashboard');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Close user dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.dh-user-wrap')) setShowUserDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch dashboard stats (re-runs when active org changes)
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const estRes = await establishmentAPI.getAll();
        const allEstablishments = estRes.establishments || [];
        const establishments = lockedEstId
          ? allEstablishments.filter(e => String(e.id) === String(lockedEstId))
          : allEstablishments;
        let totalEmployees = 0, activeEmployees = 0;

        for (const est of establishments) {
          try {
            const empRes = await employeeAPI.getByEstablishment(est.id);
            const emps = empRes.employees || [];
            totalEmployees += emps.length;
            activeEmployees += emps.filter(
              e => !e.employment_status || e.employment_status === 'active'
            ).length;
          } catch {}
        }

        const now = new Date();
        const month = now.getMonth() + 1;
        const year = now.getFullYear();
        let currentMonthPayroll = 0;

        for (const est of establishments) {
          try {
            const sumRes = await payrollAPI.getSummary(est.id, year);
            const md = sumRes.summary?.find(s => s.wage_month === month && s.wage_year === year);
            if (md) currentMonthPayroll += parseFloat(md.total_net_payable || 0);
          } catch {}
        }

        if (currentMonthPayroll === 0 && establishments.length > 0) {
          try {
            const sumRes = await payrollAPI.getSummary(establishments[0].id, year);
            if (sumRes.summary?.length > 0) {
              currentMonthPayroll = parseFloat(sumRes.summary[0].total_net_payable || 0);
            }
          } catch {}
        }

        setDashboardStats({
          totalEmployees, activeEmployees,
          activeEstablishments: lockedEstId ? 1 : establishments.length,
          currentMonthPayroll, loading: false,
        });
      } catch {
        setDashboardStats(prev => ({ ...prev, loading: false }));
      }
    };
    setDashboardStats(prev => ({ ...prev, loading: true }));
    fetchStats();
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigate = (viewId) => {
    setCurrentView(viewId);
    window.history.pushState(null, '', viewId === 'dashboard' ? '/' : `/${viewId}`);
    if (isMobile) setIsMobileSidebarOpen(false);
  };

  // ── Dashboard Home ────────────────────────────────────────────────
  const renderDashboardHome = () => {
    const role = getPrimaryRole(user);
    const isEmployee = role === 'Member';
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

    return (
      <div className="dh-home">

        {/* KPI cards — Manager/Admin only */}
        {!isEmployee && (
          dashboardStats.loading ? (
            <div className="dh-loading">
              <div className="dh-spinner" />
              <p>Loading dashboard data…</p>
            </div>
          ) : (
            <div className="dh-kpi-grid">
              <KpiCard icon={<IconEmployees />} label="Total Employees"
                value={dashboardStats.totalEmployees}
                sub={`${dashboardStats.activeEmployees} active · ${dashboardStats.totalEmployees - dashboardStats.activeEmployees} inactive`}
                colorClass="kpi-indigo" />
              <KpiCard icon={<IconBuilding />} label="Organizations"
                value={dashboardStats.activeEstablishments}
                sub="Active locations" colorClass="kpi-emerald" />
              <KpiCard icon={<IconRupee />} label="Monthly Payroll"
                value={dashboardStats.currentMonthPayroll > 0
                  ? `₹${dashboardStats.currentMonthPayroll.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                  : '—'}
                sub="Current month" colorClass="kpi-blue" />
              <KpiCard icon={<IconActivity />} label="System Status"
                value={<span className="dh-status-ok">Operational</span>}
                sub="All services running" colorClass="kpi-amber" />
            </div>
          )
        )}

        {/* ── Quick Links ── */}
        <div className="dh-qa-section">
          <h2 className="dh-section-title">Quick Links</h2>
          <div className="dh-ql-grid">
            {isEmployee ? (
              <>
                <button className="dh-ql-card dh-ql-indigo" onClick={() => handleNavigate('my-profile')}>
                  <div className="dh-ql-icon"><IconUserPlus /></div>
                  <span className="dh-ql-title">My Profile</span>
                  <span className="dh-ql-desc">View &amp; edit your details</span>
                </button>
                <button className="dh-ql-card dh-ql-emerald" onClick={() => handleNavigate('payslip')}>
                  <div className="dh-ql-icon"><IconForms /></div>
                  <span className="dh-ql-title">My Payslips</span>
                  <span className="dh-ql-desc">Download monthly payslips</span>
                </button>
                <button className="dh-ql-card dh-ql-rose" onClick={() => handleNavigate('apply-leave')}>
                  <div className="dh-ql-icon"><IconLeave /></div>
                  <span className="dh-ql-title">Apply Leave</span>
                  <span className="dh-ql-desc">Submit a leave request</span>
                </button>
              </>
            ) : (
              <>
                {canAccessPage(user, 'employees') && (
                  <button className="dh-ql-card dh-ql-indigo" onClick={() => handleNavigate('employees')}>
                    <div className="dh-ql-icon"><IconUserPlus /></div>
                    <span className="dh-ql-title">Employees</span>
                    <span className="dh-ql-desc">Manage your workforce</span>
                  </button>
                )}
                {canAccessPage(user, 'generate-payroll') && (
                  <button className="dh-ql-card dh-ql-emerald" onClick={() => handleNavigate('generate-payroll')}>
                    <div className="dh-ql-icon"><IconRupee /></div>
                    <span className="dh-ql-title">Process Payroll</span>
                    <span className="dh-ql-desc">Run monthly payroll</span>
                  </button>
                )}
                {canAccessPage(user, 'attendance-registry') && (
                  <button className="dh-ql-card dh-ql-blue" onClick={() => handleNavigate('attendance-registry')}>
                    <div className="dh-ql-icon"><IconCalendar /></div>
                    <span className="dh-ql-title">Attendance</span>
                    <span className="dh-ql-desc">Daily attendance registry</span>
                  </button>
                )}
                {canAccessPage(user, 'forms-templates') && (
                  <button className="dh-ql-card dh-ql-amber" onClick={() => handleNavigate('forms-templates')}>
                    <div className="dh-ql-icon"><IconForms /></div>
                    <span className="dh-ql-title">Generate Forms</span>
                    <span className="dh-ql-desc">Statutory compliance forms</span>
                  </button>
                )}
                {canAccessPage(user, 'payslip') && (
                  <button className="dh-ql-card dh-ql-purple" onClick={() => handleNavigate('payslip')}>
                    <div className="dh-ql-icon"><IconForms /></div>
                    <span className="dh-ql-title">View Payslips</span>
                    <span className="dh-ql-desc">Browse employee payslips</span>
                  </button>
                )}
                {canAccessPage(user, 'apply-leave') && (
                  <button className="dh-ql-card dh-ql-rose" onClick={() => handleNavigate('apply-leave')}>
                    <div className="dh-ql-icon"><IconLeave /></div>
                    <span className="dh-ql-title">Apply Leave</span>
                    <span className="dh-ql-desc">Submit a leave request</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>

      </div>
    );
  };

  // ── Route → Component ─────────────────────────────────────────────
  const renderContent = () => {
    if (currentView !== 'dashboard' && !canAccessPage(user, currentView)) {
      return renderDashboardHome();
    }
    switch (currentView) {
      case 'dashboard':             return renderDashboardHome();
      case 'organizations':         return <Establishments />;
      case 'add-organization':      return <AddEstablishment />;
      case 'update-organization':   return <UpdateEstablishment />;
      case 'employees':             return <Employees />;
      case 'new-employee':          return <NewEmployee />;
      case 'update-employee':       return <UpdateEmployee />;
      case 'my-profile':            return <MyProfile />;
      case 'reports':               return <ReportsHub />;
      case 'monthly-info-service':  return <PayrollInsights />;
      case 'forms-templates':       return <FormsTemplates onNavigate={handleNavigate} />;
      case 'view-forms':            return <ViewForms />;
      case 'form-data-review':      return <FormDataReview />;
      case 'appointment-order':     return <AppointmentOrderForm />;
      case 'form-s-uti':            return <FormSUTI />;
      case 'form-xiv':              return <FormXIV />;
      case 'form-19-payslip':       return <Form19Payslip />;
      case 'form-t-muster-roll':    return <FormTMusterRoll />;
      case 'ecr-generator':         return <ECRGenerator />;
      case 'form-x-wages':          return <FormXRegisterWages />;
      case 'form-xiii-min-wages':   return <FormXIIIMinimumWages />;
      case 'bank-statement':        return <BankStatementForm />;
      case 'form-xxiii-overtime':   return <FormXXIIIOvertime />;
      case 'form-5-earned-leave':   return <Form5EarnedLeave />;
      case 'form-u-annual-returns': return <FormUAnnualReturns />;
      case 'form-xxix-deductions':  return <FormXXIXDeductionsExcel />;
      case 'form-a-professional-tax': return <FormAProfessionalTax />;
      case 'apply-leave':           return <ApplyLeave />;
      case 'approve-leave':         return <ApproveLeave />;
      case 'analytics':             return <Analytics />;
      case 'apply-resignation':     return <ApplyResignation />;
      case 'approve-resignation':   return <ApproveResignation />;
      case 'terminate-employee':    return <TerminateEmployee />;
      case 'adjustments':            return <Adjustments />;
      case 'variable-pay':          return <VariablePay />;
      case 'overtime-work':         return <OvertimeWork />;
      case 'shift-tracker':         return <ShiftTracker />;
      case 'holiday-schedule':      return <HolidaySchedule />;
      case 'attendance-registry':   return <AttendanceRegistry />;
      case 'leave-absent-record':   return <LeaveAbsentRecord />;
      case 'monthly-payroll-registry': return <MonthlyPayrollRegistry />;
      case 'generate-payroll':      return <MonthlyPayrollRegistry />;
      case 'payslip':               return <MyPayslip />;
      case 'view-statements':       return <ViewStatements />;
      case 'admin-dashboard':       return <AdminDashboard />;
      case 'groups-management':     return <GroupsManagement />;
      case 'generate-invoice':      return <GenerateInvoice />;
      case 'view-invoices':         return <ViewInvoices />;
      case 'assets':                return <Asset />;
      case 'documents':             return <Documents />;
      case 'audit-trail':           return <AuditTrail />;
      case 'leave-settings':        return <LeaveConfigPage />;
      case 'invite-users':          return <InviteUsers />;
      case 'profile':               return <UserProfile user={user} />;
      // Barcode Attendance
      case 'barcode-scanner':       return <AttendanceScannerKiosk />;
      case 'shift-live-log':        return <ShiftLiveLog />;
      case 'my-barcode':            return <MyBarcode />;
      case 'my-shift-history':      return <MyShiftHistory />;
      case 'my-attendance':         return <MyAttendance />;
      default:
        return renderDashboardHome();
    }
  };

  const pageMeta = VIEW_META[currentView];
  const userInitials = (user?.username || user?.name || 'U').slice(0, 2).toUpperCase();

  return (
    <div className="dashboard-container">
      <Sidebar
        currentView={currentView}
        onNavigate={handleNavigate}
        isMobile={isMobile}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
      />

      <div className="dashboard-main">
        {/* ── Top Bar ── */}
        <header className="app-topbar">
          {/* Mobile hamburger */}
          {isMobile && (
            <button
              className="app-hamburger"
              onClick={() => setIsMobileSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <Menu size={20} />
            </button>
          )}

          {/* Breadcrumb / context */}
          <div className="app-topbar-left">
            {currentView !== 'dashboard' && (
              <nav className="app-breadcrumb" aria-label="Breadcrumb">
                <button className="app-bc-home" onClick={() => handleNavigate('dashboard')}>
                  <Home size={13} />
                  {!isMobile && <span>Home</span>}
                </button>
                {pageMeta?.section && (
                  <>
                    <span className="app-bc-sep">/</span>
                    <span className="app-bc-item">{pageMeta.section}</span>
                  </>
                )}
                <span className="app-bc-sep">/</span>
                <span className="app-bc-current">{pageMeta?.title || currentView}</span>
              </nav>
            )}
            {currentView === 'dashboard' && !isMobile && (
              <span className="app-greeting">
                Good {getTimeOfDay()}, <strong>{user?.username || user?.name || 'there'}</strong>
              </span>
            )}
          </div>

          {/* Right actions */}
          <div className="app-topbar-right">
            <OrgSwitcher />
            <ThemeSwitcher />

            <button className="app-topbar-btn" aria-label="Notifications">
              <Bell size={17} />
              <span className="app-notif-badge" aria-label="3 notifications">3</span>
            </button>

            <div className="dh-user-wrap">
              <button
                className="app-avatar-chip"
                onClick={() => setShowUserDropdown(v => !v)}
                aria-label="User menu"
                aria-expanded={showUserDropdown}
              >
                <div className="app-chip-avatar">{userInitials}</div>
                {!isMobile && (
                  <span className="app-chip-name">{user?.username || user?.name || 'User'}</span>
                )}
              </button>

              {showUserDropdown && (
                <div className="user-dropdown">
                  {/* Avatar + identity */}
                  <div className="dd-profile-block">
                    <div className="dd-avatar-lg">{userInitials}</div>
                    <div className="dd-identity">
                      <span className="dd-name">{user?.username || user?.name || 'User'}</span>
                      <span className="dd-role">{user?.role || 'Member'}</span>
                      {user?.establishment_name && (
                        <span className="dd-org">{user.establishment_name}</span>
                      )}
                      {user?.email && <span className="dd-email">{user.email}</span>}
                    </div>
                  </div>

                  <div className="dd-divider" />

                  {/* Nav items */}
                  <button className="dd-item" onClick={() => { setShowUserDropdown(false); handleNavigate('my-profile'); }}>
                    <span className="dd-item-icon">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    </span>
                    <span>My Profile</span>
                  </button>

                  <div className="dd-divider" />

                  {/* Sign out */}
                  <button className="dd-signout" onClick={logout}>
                    <LogOut size={15} />
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Page Content ── */}
        <main className="dashboard-content">
          {renderContent()}
        </main>
      </div>

      {/* Floating AI chat */}
      <ChatPopup />
    </div>
  );
};

export default Dashboard;
